<template>
  <div ref="boxComponent" class="container box_component judge-panel">
    <div class="header-title">
      <div class="header-title-main">
        <h5 class="panel-title">{{ modeLabel }}</h5>
        <span class="beta-tag">{{ $t('training.judge.beta') }}</span>
      </div>
      <div class="header-actions">
        <button
          class="compare-rounds-btn"
          type="button"
          :disabled="!canOpenCompareModal"
          @click="openCompareModal"
        >
          <i class="fa-solid fa-columns"></i> {{ $t('training.judge.compare_launch') || 'Comparar rondas' }}
        </button>
      </div>
    </div>

    <p class="muted" v-if="isLoading">{{ $t('training.loading') }}</p>
    <p class="muted" v-else-if="!rounds.length">{{ $t('training.judge.empty') }}</p>

    <div class="training-grid" v-else :class="rounds.length <= 2 ? 'single' : ''">
      <div
        v-for="round in rounds"
        :key="round.id"
        class="training-card"
        :class="getStatusClass(round.status_id)"
        @click.prevent="handleCardClick(round)"
      >
        <div class="card-header" role="button" tabindex="0">
          <img :src="round.video_link ? youtubeVideo : notVideo" alt="Round thumbnail" class="card-image">
        </div>
        <div class="card-body" role="button" tabindex="0">
          <p class="status-text">{{ round.status?.name || '—' }}</p>
          <p><strong>{{ $t('training.rules') }}:</strong> {{ round.rule?.name || '—' }}</p>
          <p><strong>{{ $t('training.round_name') }}:</strong> {{ round.round_name || '—' }}</p>
          <p><strong>{{ $t('training.total_score') }}:</strong> {{ formatScore(round.total_score) }}</p>
          <p v-if="round.assigned_judge"><strong>{{ $t('training.assigned_judge') }}:</strong> {{ judgeName(round.assigned_judge) }}</p>
        </div>
        <div class="card-footer" @click.stop>
          <button
            :disabled="!isFinishedRound(round)"
            class="action-btn edit"
            type="button"
            @click.stop="editRound(round)"
            :title="$t('training.btn_edit')"
          >
            <i class="fa-solid fa-pencil-alt"></i>
          </button>
          <button
            :disabled="true"
            class="action-btn delete"
            type="button"
            :title="$t('training.btn_delete')"
          >
            <i class="fa-solid fa-trash"></i>
          </button>
        </div>
      </div>
    </div>

    <div v-if="showPagination" class="rounds-pagination">
      <button
        type="button"
        class="pagination-btn"
        :disabled="pagination.current_page <= 1 || isLoading"
        @click="changePage(pagination.current_page - 1)"
      >
        {{ $t('training.pagination.prev') || 'Anterior' }}
      </button>

      <span class="pagination-summary">
        {{ $t('training.pagination.page') || 'Página' }} {{ pagination.current_page }}
        {{ $t('training.pagination.of') || 'de' }} {{ pagination.last_page }}
      </span>

      <button
        type="button"
        class="pagination-btn"
        :disabled="pagination.current_page >= pagination.last_page || isLoading"
        @click="changePage(pagination.current_page + 1)"
      >
        {{ $t('training.pagination.next') || 'Siguiente' }}
      </button>
    </div>

    <JudgeTrainingComparePanel
      :visible="compareVisible"
      :left-options="judgeCompareLeftOptions"
      :left-round-id="compareSelection.leftRoundId"
      :right-options="judgeCompareRightOptions"
      :right-round-id="compareSelection.rightRoundId"
      :compare-with-original="compareSelection.compareWithOriginal"
      :original-option="judgeCompareOriginalOption"
      :original-disabled-reason="judgeCompareOriginalDisabledReason"
      :other-judge-disabled-reason="judgeCompareOtherJudgeDisabledReason"
      :options-loading="compareOptionsLoading"
      :loading="compareLoading"
      :payload="comparePayload"
      :error="compareError"
      @close="closeCompareModal"
      @select-left="setCompareLeftRound"
      @select-right="setCompareRightRound"
      @toggle-original="setCompareOriginalMode"
      @compare="fetchCompareRounds"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import JudgeTrainingComparePanel from '@/components/training/JudgeTrainingComparePanel.vue';
import router from '@/router/router';
import { resolveResponsiveGridItemsPerPage } from '@/helpers/responsiveGridPagination';

export default {
  name: 'JudgeTrainingPanel',
  components: {
    JudgeTrainingComparePanel,
  },
  props: {
    modeLabel: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      notVideo: require('@/assets/videoOff.jpg'),
      youtubeVideo: require('@/assets/videoOn.jpg'),
      rounds: [],
      isLoading: false,
      itemsPerPage: 6,
      currentPage: 1,
      pagination: null,
      resizeObserver: null,
      resizeFrame: null,
      observedContainerWidth: 0,
      compareVisible: false,
      compareOptionsLoading: false,
      compareLoading: false,
      compareError: '',
      compareOptions: [],
      comparePayload: null,
      compareSelection: {
        leftRoundId: '',
        rightRoundId: '',
        compareWithOriginal: false,
      },
    };
  },
  computed: {
    ...mapState('user', ['user', 'token']),
    showPagination() {
      return Boolean(this.pagination && Number(this.pagination.last_page) > 1);
    },
    judgeCompareLeftOptions() {
      return (this.compareOptions || []).map((option) => ({
        id: option.id,
        label: option.label,
      }));
    },
    selectedCompareLeftEntry() {
      const leftRoundId = Number(this.compareSelection.leftRoundId || 0);
      if (!leftRoundId) return null;
      return (this.compareOptions || []).find((option) => Number(option.id) === leftRoundId) || null;
    },
    judgeCompareRightOptions() {
      return Array.isArray(this.selectedCompareLeftEntry?.other_judge_options)
        ? this.selectedCompareLeftEntry.other_judge_options
        : [];
    },
    judgeCompareOriginalOption() {
      const originalOption = this.selectedCompareLeftEntry?.original_option;
      if (!originalOption?.available || !originalOption?.id) return null;
      return {
        id: originalOption.id,
        label: originalOption.label,
      };
    },
    judgeCompareOriginalDisabledReason() {
      if (!this.compareSelection.leftRoundId) {
        return this.$t('training.judge.compare_select_left_first') || 'Selecciona primero una ronda a la izquierda';
      }

      return this.selectedCompareLeftEntry?.original_option?.disabled_reason
        || '';
    },
    judgeCompareOtherJudgeDisabledReason() {
      if (!this.compareSelection.leftRoundId) {
        return this.$t('training.judge.compare_select_left_first') || 'Selecciona primero una ronda a la izquierda';
      }

      if (this.judgeCompareRightOptions.length) {
        return '';
      }

      return this.$t('training.judge.compare_no_other_judges') || 'No hay otras evaluaciones finalizadas de esta misma ronda.';
    },
    canOpenCompareModal() {
      return !this.isLoading;
    }
  },
  mounted() {
    this.fetchTrainingRounds();
    this.setupResponsivePagination();
  },
  beforeUnmount() {
    this.teardownResponsivePagination();
  },
  methods: {
    ...mapActions('training', ['setTrainingRoundsIdToEdit', 'setTrainingRoundsIdToView', 'clearTrainingRoundsIdToEdit', 'clearTrainingRoundsIdToView']),
    ...mapActions('competition', ['updateAppNavigationZone']),
    setupResponsivePagination() {
      this.$nextTick(() => {
        this.scheduleItemsPerPageUpdate();
        window.addEventListener('resize', this.scheduleItemsPerPageUpdate);
        window.addEventListener('orientationchange', this.scheduleItemsPerPageUpdate);

        if (typeof ResizeObserver === 'undefined') {
          return;
        }

        const target = this.$refs.boxComponent;
        if (!target) {
          return;
        }

        this.resizeObserver = new ResizeObserver((entries) => {
          const entry = entries?.[0];
          const nextWidth = Math.round(entry?.contentRect?.width || 0);
          if (!nextWidth || nextWidth === this.observedContainerWidth) {
            return;
          }

          this.observedContainerWidth = nextWidth;
          this.scheduleItemsPerPageUpdate();
        });
        this.resizeObserver.observe(target);
      });
    },
    teardownResponsivePagination() {
      window.removeEventListener('resize', this.scheduleItemsPerPageUpdate);
      window.removeEventListener('orientationchange', this.scheduleItemsPerPageUpdate);
      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = null;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },
    scheduleItemsPerPageUpdate() {
      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
      }

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.updateItemsPerPage();
      });
    },
    async updateItemsPerPage() {
      const target = this.$refs.boxComponent;
      if (!target) {
        return;
      }

      const containerWidth = Math.round(target.clientWidth || 0);
      if (!containerWidth) {
        return;
      }

      this.observedContainerWidth = containerWidth;
      const nextItemsPerPage = resolveResponsiveGridItemsPerPage(containerWidth, window.innerWidth, {
        desktopMinCardWidth: 280,
        mobileMinCardWidth: 280,
        desktopGridGap: 15,
        mobileGridGap: 15,
        rowsPerPage: 2,
      });

      if (nextItemsPerPage === this.itemsPerPage) {
        return;
      }

      const currentOffset = Math.max(this.currentPage - 1, 0) * this.itemsPerPage;
      this.itemsPerPage = nextItemsPerPage;
      const nextPage = Math.max(1, Math.floor(currentOffset / this.itemsPerPage) + 1);
      await this.fetchTrainingRounds(nextPage);
    },
    async fetchTrainingRounds(page = this.currentPage) {
      this.isLoading = true;
      try {
        const response = await this.$axios.get('/training/list', {
          params: {
            type: 'judge',
            page: Number(page) || 1,
            per_page: this.itemsPerPage,
          },
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });
        const payload = response?.data;
        const items = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.data) ? payload.data : []);

        this.rounds = items;
        this.currentPage = Number(page) || 1;
        this.pagination = this.normalizePagination(payload?.pagination || null, this.currentPage, this.itemsPerPage);
      } catch (error) {
        console.error('❌ Error al obtener entrenamientos de juez:', error);
        this.rounds = [];
        this.pagination = null;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchCompareOptions() {
      this.compareOptionsLoading = true;
      this.compareError = '';

      try {
        const response = await this.$axios.get('/training/judge/compare-options', {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });

        const payload = response?.data;
        this.compareOptions = Array.isArray(payload?.data) ? payload.data : [];
        this.ensureCompareSelection();
      } catch (error) {
        console.error('❌ Error al obtener opciones de comparación de juez:', error);
        this.compareOptions = [];
        this.compareError = error?.response?.data?.message || this.$t('training.judge.compare_load_error') || 'No se pudieron cargar las opciones de comparación.';
      } finally {
        this.compareOptionsLoading = false;
      }
    },
    openCompareModal() {
      this.compareVisible = true;
      this.comparePayload = null;
      this.fetchCompareOptions();
    },
    closeCompareModal() {
      this.compareVisible = false;
      this.compareLoading = false;
      this.compareError = '';
    },
    ensureCompareSelection() {
      const leftOptions = this.judgeCompareLeftOptions;
      const selectedLeftRoundId = Number(this.compareSelection.leftRoundId || 0);
      const selectedLeftExists = leftOptions.some((option) => Number(option.id) === selectedLeftRoundId);
      const nextLeftRoundId = selectedLeftExists
        ? selectedLeftRoundId
        : Number(leftOptions[0]?.id || 0);

      this.compareSelection = {
        ...this.compareSelection,
        leftRoundId: nextLeftRoundId || '',
      };

      this.syncRightCompareSelection();
    },
    syncRightCompareSelection() {
      const compareWithOriginal = Boolean(this.compareSelection.compareWithOriginal);
      const currentRightRoundId = Number(this.compareSelection.rightRoundId || 0);

      if (compareWithOriginal) {
        const originalId = Number(this.judgeCompareOriginalOption?.id || 0);
        this.compareSelection = {
          ...this.compareSelection,
          rightRoundId: originalId || '',
        };
        return;
      }

      const rightOptions = this.judgeCompareRightOptions;
      const hasCurrentRight = rightOptions.some((option) => Number(option.id) === currentRightRoundId);
      this.compareSelection = {
        ...this.compareSelection,
        rightRoundId: hasCurrentRight ? currentRightRoundId : (Number(rightOptions[0]?.id || 0) || ''),
      };
    },
    setCompareLeftRound(value) {
      this.compareSelection = {
        ...this.compareSelection,
        leftRoundId: Number(value || 0) || '',
      };
      this.comparePayload = null;
      this.compareError = '';
      this.syncRightCompareSelection();
    },
    setCompareRightRound(value) {
      this.compareSelection = {
        ...this.compareSelection,
        rightRoundId: Number(value || 0) || '',
      };
      this.comparePayload = null;
      this.compareError = '';
    },
    setCompareOriginalMode(value) {
      this.compareSelection = {
        ...this.compareSelection,
        compareWithOriginal: Boolean(value),
      };
      this.comparePayload = null;
      this.compareError = '';
      this.syncRightCompareSelection();
    },
    async fetchCompareRounds() {
      const leftRoundId = Number(this.compareSelection.leftRoundId || 0);
      const rightRoundId = Number(this.compareSelection.rightRoundId || 0);

      if (!leftRoundId || !rightRoundId) {
        this.compareError = this.$t('training.supervisor.compare_missing_selection') || 'Selecciona una ronda para cada lado.';
        return;
      }

      this.compareLoading = true;
      this.compareError = '';

      try {
        const response = await this.$axios.post('/training/judge/compare-rounds', {
          left_round_id: leftRoundId,
          right_round_id: rightRoundId,
        }, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });

        if (!response?.data?.success) {
          throw new Error(this.$t('training.supervisor.compare_error') || 'No se pudo cargar la comparación.');
        }

        this.comparePayload = response.data;
      } catch (error) {
        console.error('❌ Error al comparar rondas de juez:', error);
        this.comparePayload = null;
        this.compareError = error?.response?.data?.message || this.$t('training.supervisor.compare_error') || 'No se pudo cargar la comparación.';
      } finally {
        this.compareLoading = false;
      }
    },
    normalizePagination(rawPagination, fallbackPage = 1, fallbackPerPage = 6) {
      const source = rawPagination && typeof rawPagination === 'object' ? rawPagination : {};
      const currentPage = Number(source.current_page ?? source.currentPage ?? fallbackPage) || fallbackPage;
      const perPage = Number(source.per_page ?? source.perPage ?? fallbackPerPage) || fallbackPerPage;
      const total = Number(source.total ?? source.total_items ?? source.totalItems ?? 0) || 0;
      const count = Number(source.count ?? source.item_count ?? source.itemCount ?? 0) || 0;
      const lastPage = Number(source.last_page ?? source.lastPage ?? (perPage > 0 ? Math.ceil(total / perPage) : 1)) || 1;

      return {
        current_page: Math.max(1, currentPage),
        per_page: Math.max(1, perPage),
        total: Math.max(0, total),
        count: Math.max(0, count),
        last_page: Math.max(1, lastPage),
        has_more_pages: Boolean(source.has_more_pages ?? source.hasMorePages ?? currentPage < lastPage),
        next_page: Number(source.next_page ?? source.nextPage ?? (currentPage < lastPage ? currentPage + 1 : null)) || null,
        prev_page: Number(source.prev_page ?? source.prevPage ?? (currentPage > 1 ? currentPage - 1 : null)) || null,
      };
    },
    async changePage(page) {
      const requestedPage = Number(page) || 1;
      if (requestedPage <= 0) return;
      if (requestedPage === this.currentPage) return;

      await this.fetchTrainingRounds(requestedPage);
    },
    judgeName(judge) {
      if (!judge) return '';
      const parts = [judge.name, judge.surname].filter(Boolean);
      return parts.join(' ');
    },
    formatScore(score) {
      const value = parseFloat(score);
      return Number.isFinite(value) ? value.toFixed(2) : '—';
    },
    getStatusClass(statusId) {
      switch (statusId) {
        case 3:
          return 'status-pending';
        case 1:
          return 'status-registered';
        case 4:
          return 'status-approved';
        case 10:
          return 'status-published';
        case 15:
          return 'status-started';
        case 8:
          return 'status-finished';
        default:
          return 'status-unknown';
      }
    },
    isFinishedRound(round) {
      return Number(round?.status_id) === 8;
    },
    async handleCardClick(round) {
      if (this.isFinishedRound(round)) {
        await this.viewRound(round);
        return;
      }
      await this.editRound(round);
    },
    async viewRound(round) {
      try {
        await this.clearTrainingRoundsIdToEdit();
        await this.setTrainingRoundsIdToView(round.id);
        this.updateAppNavigationZone({
          zone: 'training',
          data: { cameFromTrainingZone: true },
        });
        await router.push('/score');
      } catch (error) {
        console.error('❌ Error al abrir vista de ronda de juez:', error);
      }
    },
    async editRound(round) {
      try {
        await this.clearTrainingRoundsIdToView();
        await this.setTrainingRoundsIdToEdit(round.id);
        this.updateAppNavigationZone({
          zone: 'training',
          data: { cameFromTrainingZone: true },
        });
        await router.push('/score');
      } catch (error) {
        console.error('❌ Error al abrir ronda de juez:', error);
      }
    }
  }
};
</script>

<style scoped>
.box_component {
  width: 80%;
  max-width: 80%;
  margin: 15px auto;
}

@media (max-width: 576px) {
  .box_component {
    width: 100%;
    max-width: 100%;
  }
}
.header-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.header-title-main {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.panel-title {
  margin: 0;
  font-weight: 600;
  color: #374151;
}
.compare-rounds-btn {
  background: #0f766e;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.compare-rounds-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.compare-rounds-btn:not(:disabled):hover {
  background: #115e59;
}
.beta-tag {
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 999px;
  background: #e0f2fe;
  color: #0b5ed7;
  border: 1px solid #cfe2ff;
}
.lead {
  color: #4b5563;
  margin-bottom: 16px;
}
.training-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 15px;
  justify-content: center;
  margin: 0 auto;
}

.rounds-pagination {
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.pagination-btn {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 8px;
  padding: 8px 12px;
  font-weight: 600;
  cursor: pointer;
}

.pagination-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.pagination-summary {
  color: #4b5563;
  font-size: 13px;
  font-weight: 600;
}

.training-card {
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  text-align: center;
  background: #fff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: box-shadow 0.3s ease;
  margin-bottom: 15px;
  width: 100%;
  max-width: 320px;
  justify-self: center;
  cursor: pointer;
}

.status-pending {
  box-shadow: 0 0 10px rgba(255, 165, 0, 0.6);
}
.status-approved {
  box-shadow: 0 0 10px rgba(0, 128, 0, 0.6);
}
.status-published {
  box-shadow: 0 0 10px rgba(0, 0, 255, 0.6);
}
.status-started {
  box-shadow: 0 0 10px rgba(255, 215, 0, 0.6);
}
.status-finished {
  box-shadow: 0 0 10px rgba(128, 128, 128, 0.6);
}
.status-registered {
  box-shadow: 0 0 10px rgba(66, 60, 98, 0.6);
}
.status-unknown {
  box-shadow: 0 0 10px rgba(145, 23, 23, 0.6);
}

.card-image {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  opacity: 0.7;
}

.card-body {
  padding: 10px 12px 14px;
}

.status-text {
  margin: 0 0 6px;
  font-weight: 600;
  color: #374151;
}

.card-body p {
  margin: 0 0 4px;
  color: #6b7280;
  font-size: 13px;
}

.muted {
  color: #6b7280;
  font-size: 13px;
  margin: 0 0 4px;
}

.card-body strong {
  color: #374151;
}

.card-footer {
  display: flex;
  justify-content: space-around;
  padding: 10px;
  background: #f8f9fa;
  border-top: 1px solid #ddd;
}

.action-btn {
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 5px;
  color: #5b5b5b;
  opacity: 0.9;
}

.action-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.action-btn i {
  color: gray !important;
}

@media (max-width: 576px) {
  .training-card {
    width: 100%;
    max-width: 320px;
    margin: 0 auto;
  }
}
</style>
